<template>
    <section v-if="rows.length > 0" class="relationships">
        <h3 class="relationships__heading">Relationships</h3>

        <!-- A house or dealership holding none of its own resolves through the
             Operation. Saying so is the difference between a gap and a design. -->
        <p v-if="inherited" class="relationships__inherited">
            Falls through to <strong>{{ inherited.name }}</strong
            ><template v-if="inherited.legalCompany">
                — legally {{ inherited.legalCompany.name }}</template
            ><template v-if="inherited.billingCompany">, billed to
                {{ inherited.billingCompany.name }}</template
            >.
        </p>

        <div v-for="row in rows" :key="row.key" class="relationships__row">
            <div class="relationships__label">
                <span>{{ row.label }}</span>
                <small>{{ row.help }}</small>
            </div>

            <component
                :is="EDITORS[row.kind]"
                v-if="EDITORS[row.kind]"
                :key="`${row.key}-${versionOf(row.key)}`"
                :row="row"
                :record="record"
                :store="store"
                :editing="editing"
                @changed="() => refresh(row.key)"
            />

            <div v-else class="relationships__control">
                <DocumentPicker
                    v-if="editing"
                    :key="`${row.key}-${versionOf(row.key)}`"
                    :client="row.client"
                    :model-value="row.read(record)?.companyId ?? null"
                    :current-label="row.read(record)?.name ?? ''"
                    :placeholder="`Search ${row.label.toLowerCase()}…`"
                    :disabled="busy === row.key"
                    :clearable="row.kind !== 'operation'"
                    @update:model-value="(value) => onPick(row, value)"
                />
                <span v-else class="relationships__value">{{ row.read(record)?.name ?? '—' }}</span>

                <InputText
                    v-if="row.kind === 'billing' && editing"
                    v-model="subscription"
                    :placeholder="row.readSubscription(record) || 'sub_…'"
                    :disabled="busy === row.key"
                    class="relationships__subscription"
                    aria-label="Stripe subscription"
                />
                <!-- Only when there is one. A payer that is not named has no
                     subscription either, and two dashes in a row read as a
                     fault rather than as an empty pair. -->
                <span
                    v-else-if="row.kind === 'billing' && row.readSubscription(record)"
                    class="relationships__value relationships__value--quiet"
                >{{ row.readSubscription(record) }}</span>

                <Tag
                    v-if="row.kind === 'billing' && row.readStanding(record)"
                    :value="enumLabel('standing', row.readStanding(record))"
                    severity="secondary"
                />
            </div>

            <Message
                v-if="failure && failure.key === row.key && !EDITORS[row.kind]"
                severity="error"
                :closable="false"
                class="relationships__failure"
            >
                {{ failureText(failure.error) }}
            </Message>
        </div>
    </section>
</template>

<script setup>
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Tag from 'primevue/tag'
import { computed, ref } from 'vue'

import { enumLabel } from '@/lib/enumMeta.js'
import { failureText } from '@/lib/failureText.js'
import { inheritedFrom, RELATIONSHIPS } from '@/lib/relationships.js'

import DocumentPicker from './DocumentPicker.vue'
import ManagedRosterList from './ManagedRosterList.vue'
import MemberSetEditor from './MemberSetEditor.vue'
import PolicyEditor from './PolicyEditor.vue'
import ReplaceListEditor from './ReplaceListEditor.vue'

// The relationships one document holds, each with the action that changes it.
//
// Every row writes immediately rather than joining the panel's draft: these are
// their own routes on the server, each with its own invariants — clearing a
// legal Company can be refused because a house depends on it, and naming a
// payer can be refused because that Company carries no billing facts. A draft
// that batched them would report one refusal for several edits and leave the
// caller guessing which.
const props = defineProps({
    record: { type: Object, required: true },
    collection: { type: String, required: true },
    store: { type: Object, required: true },
    // The panel's own edit flag. False is the side rail, which is for looking —
    // and these rows write the moment they are touched, so a picker there was
    // an unasked-for edit one click away from a record somebody opened to read.
    editing: { type: Boolean, default: false }
})

const rows = RELATIONSHIPS[props.collection] ?? []

// Per-row, so one failing edit does not blank the others' state.
const busy = ref('')
const failure = ref(null)

/**
 * What the box holds, which is not always what gets sent.
 *
 * Empty means "the one this document already has". The server requires a
 * subscription whenever a payer is named, and the commonest edit on this row is
 * changing the payer and nothing else — which used to send an empty string and
 * earn a `REQUEST_VALIDATION_FAILED` about a field nobody had touched.
 */
const subscription = ref('')

function subscriptionToSend(row) {
    const typed = subscription.value.trim()
    if (typed !== '') {
        return typed
    }
    return row.readSubscription(props.record)
}

/**
 * Bumped after every attempt, and used to key the controls.
 *
 * A `Select` holds what the user picked, and the record it was built from does
 * not change when the server refuses — so nothing tells Vue to put it back, and
 * a refused clear leaves the box empty while the document still holds the
 * value. Remounting on every attempt makes each control derive from the record
 * again, which is the only thing that knows the truth.
 *
 * Per row, not one counter for the panel. A single counter keyed every control
 * on the panel, so naming a legal company remounted the policy editor beside it
 * and threw away the JSON somebody was halfway through typing.
 */
const versions = ref({})

function versionOf(key) {
    return versions.value[key] ?? 0
}

function bump(key) {
    versions.value = { ...versions.value, [key]: versionOf(key) + 1 }
}

async function apply(row, value, extra) {
    busy.value = row.key
    failure.value = null
    const ok = await row.apply(props.store, props.record._id, value, extra)
    if (ok === false) {
        failure.value = { key: row.key, error: props.store.error }
    } else {
        // The panel reads from the page the list is holding, and a relationship
        // write does not touch it — so without this a successful edit shows the
        // old value until something else refetches. The store re-runs whichever
        // read produced the page; asking for one by name here would be guessing
        // on its behalf.
        await props.store.refresh()
        // Emptied so the box goes back to meaning "the one this document already
        // has". Left filled, it kept sending a subscription somebody typed for a
        // payer they have since replaced.
        subscription.value = ''
    }
    bump(row.key)
    busy.value = ''
}

function onPick(row, companyId) {
    if (row.kind === 'billing') {
        // Clearing sends the one key alone; naming a payer sends both. The
        // server takes one shape or the other and nothing between.
        if (companyId === null) {
            apply(row, null, undefined)
            return
        }
        const sending = subscriptionToSend(row)
        // Named here rather than sent. A record with no payer yet has no
        // subscription to fall back on, so a first payer picked with the box
        // empty used to post `''` against `^sub_[A-Za-z0-9]+$` — a refusal
        // about a field the picker never mentioned.
        if (sending === '') {
            failure.value = {
                key: row.key,
                error: { code: 'A subscription is required', detail: 'Type the Stripe subscription beside the payer.' }
            }
            bump(row.key)
            return
        }
        apply(row, companyId, sending)
        return
    }
    apply(row, companyId, undefined)
}

// Computed, not read once. This describes the Operation the record points at,
// and that is one of the things this panel changes — held as a constant it kept
// naming the old one after a successful move.
const inherited = computed(() => inheritedFrom(props.record))

// The reference rows are pickers; the rest bring their own editor, because a
// set is not a reference and a picker cannot stand for one.
//
// `managed` is the one that edits nothing. It reads what an Operation manages
// out of the collection that holds it, and it sits here rather than beside the
// panel because the row is declared with the others and a second place to
// declare one is a second place to forget.
const EDITORS = {
    managed: ManagedRosterList,
    memberSet: MemberSetEditor,
    replaceList: ReplaceListEditor,
    policies: PolicyEditor
}

/**
 * After any of those writes, the page the panel reads from is stale for the
 * same reason a reference write leaves it stale — none of these routes touch
 * the list. Re-sending the held query is what puts the new entry on screen.
 */
async function refresh(key) {
    await props.store.refresh()
    bump(key)
}
</script>

<style scoped>
.relationships {
    border-top: 1px solid var(--p-content-border-color);
    margin-top: 16px;
    padding-top: 16px;
}

.relationships__heading {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    margin: 0 0 4px;
    text-transform: uppercase;
    color: var(--p-surface-400);
}

.relationships__inherited {
    color: var(--p-text-muted-color);
    font-size: 13px;
    margin: 0 0 12px;
}

/* Stacked, not two columns. The instance panel is a narrow rail by default —
   a label column wide enough to read squeezed every control to nothing. */
.relationships__row {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 0;
}

.relationships__label {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.relationships__label span {
    font-size: 13px;
    font-weight: 600;
}

.relationships__label small {
    color: var(--p-surface-400);
    font-size: 11px;
    line-height: 1.4;
}

.relationships__control {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.relationships__subscription {
    flex: 1 1 180px;
    min-width: 0;
}

.relationships__value {
    font-size: 13px;
}

.relationships__value--quiet {
    color: var(--p-text-muted-color);
    font-family: var(--p-font-mono);
    font-size: 12px;
}

.relationships__failure {
    margin: 4px 0 0;
}
</style>
